import React from "react";
import { showJsonViewer } from "../../services/json-viewer-service";
import "./json-cell-renderer.scss";

export interface JsonCellRendererProps {
  value: unknown;
  title?: string;
}

/**
 * Cell Renderer for JSON data.
 * Displays a truncated preview with a clickable link to open the full JSON in a modal.
 */
export const JsonCellRenderer: React.FC<JsonCellRendererProps> = ({
  value,
  title,
}) => {
  if (value === undefined || value === null) {
    return <span className="json-cell-empty">-</span>;
  }

  const jsonString = typeof value === "string" ? value : JSON.stringify(value);
  const preview = truncateJson(jsonString, 60);
  const isExpandable = jsonString.length > 60;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    showJsonViewer({
      data: value,
      title: title || "JSON Data",
    });
  };

  return (
    <span className="json-cell-renderer">
      {isExpandable ? (
        <span className="json-preview-link" onClick={handleClick}>
          {preview}
          <span className="json-expand-indicator">…</span>
        </span>
      ) : (
        <span className="json-preview-text">{preview}</span>
      )}
    </span>
  );
};

/**
 * Truncates a JSON string to a specified length, attempting to break at a sensible point.
 */
function truncateJson(json: string, maxLen: number): string {
  if (json.length <= maxLen) {
    return json;
  }

  const truncated = json.substring(0, maxLen);
  // Try to find the last complete token (brace, bracket, or quote)
  const lastBrace = truncated.lastIndexOf("}");
  const lastBracket = truncated.lastIndexOf("]");
  const lastComma = truncated.lastIndexOf(",");

  const cutPoint = Math.max(lastBrace, lastBracket, lastComma);
  if (cutPoint > maxLen * 0.7) {
    return truncated.substring(0, cutPoint + 1);
  }

  return truncated;
}
